import { ButtonIcon } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useOutlet } from 'react-router-dom';
import { playerIdAtom } from '@/atoms/player/player';
import { unwrappedPlayerDashboardWidgetsAtom } from '@/atoms/queries/userConfigs';
import { rightPanelHeaderAtom } from '@/atoms/sidebar';
import { ContentState } from '@/components/contentState/ContentState';
import { DashboardWidgets } from '@/components/dashboard/DashboardWidgets';
import { DashboardContentHeading } from '@/components/layout/DashboardContent';
import { OnboardingTooltip } from '@/components/onboarding/OnboardingTooltip';
import { StatusTranslationKey } from '@/types/status';
import { OnboardingItemKey } from '@/types/userConfigs';
import { isNullish } from '@/utils/predicates';

export const PlayerDashboardWithData = () => {
  const { t } = useTranslation(['navigation', 'general', 'onboarding']);
  const playerId = useAtomValue(playerIdAtom);
  const playerDashboardWidgets = useAtomValue(unwrappedPlayerDashboardWidgetsAtom);
  const outlet = useOutlet();
  const navigate = useNavigate();
  const rightPanelHeader = useAtomValue(rightPanelHeaderAtom);
  const preHeadingActions = outlet ? (
    // Same route-context constraint as the Summary button — see comment below.
    <ButtonIcon
      icon="DoubleChevronRight"
      size="small"
      displayText="right"
      variant="ghost"
      onClick={rightPanelHeader?.onClose ?? (() => navigate(-1))}
    >
      {t('filters.other', { ns: 'general' })}
    </ButtonIcon>
  ) : undefined;

  const summaryButton = outlet ? (
    <ButtonIcon
      icon="AiSummary"
      size="small"
      displayText="right"
      variant="secondary"
      // onClose is navigate('..') from PlayerDashboardSummaryPanel, which runs at the
      // 'summary' subroute level — that's why it navigates correctly to 'dashboard'.
      // navigate('..') called here (at the 'dashboard' level) would go to the player page instead.
      // The ?? fallback covers the render before the panel's useEffect sets the atom.
      onClick={rightPanelHeader?.onClose ?? (() => navigate(-1))}
    >
      {t('secondary.summary', { ns: 'navigation' })}
    </ButtonIcon>
  ) : (
    <OnboardingTooltip
      itemKey={OnboardingItemKey.SUMMARY_BETA}
      header={t('summaryBeta.header', { ns: 'onboarding' })}
      content={t('summaryBeta.content', { ns: 'onboarding' })}
    >
      <Link to="summary">
        <ButtonIcon icon="AiSummary" size="small" displayText="right" variant="secondary" as="span">
          {t('secondary.summary', { ns: 'navigation' })}
        </ButtonIcon>
      </Link>
    </OnboardingTooltip>
  );

  let content;
  if (playerDashboardWidgets === undefined) {
    content = <ContentState status={StatusTranslationKey.LOADING} />;
  } else if (isNullish(playerId)) {
    content = <ContentState status={StatusTranslationKey.ERROR} />;
  } else {
    content = <DashboardWidgets entityId={playerId} widgets={playerDashboardWidgets} isNarrow={!!outlet} />;
  }

  return (
    <DashboardContentHeading preHeadingActions={preHeadingActions} headerActions={summaryButton}>
      {content}
    </DashboardContentHeading>
  );
};
